import React from 'react';
import { useGetList } from 'react-admin';
import {
	Box,
	Card,
	CardContent,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { formatEur } from '../../Utils/money';
import { OrphanUsageRow } from './billingAlertsState';
import { MeasuredAmount } from './cells';
import { densitySx } from './columnLayout';
import { PeriodRange } from './PeriodFilter';

const ORPHANS_PER_PAGE = 200;

const sum = (rows: OrphanUsageRow[], key: 'storageEur' | 'totalEur'): number =>
	rows.reduce((total, row) => total + (row[key] || 0), 0);

const OrphanRow: React.FC<{ row: OrphanUsageRow }> = ({ row }) => (
	<TableRow>
		<TableCell>
			<Typography component="span" fontFamily="monospace" fontSize="inherit">
				{row.folderId}
			</Typography>
		</TableCell>
		<TableCell align="right">
			<MeasuredAmount value={row.storageEur} measuredDays={row.measuredDays} />
		</TableCell>
		<TableCell align="right">
			<MeasuredAmount value={row.totalEur} measuredDays={row.measuredDays} />
		</TableCell>
	</TableRow>
);

/**
 * Les consos dont le projet a été effacé de qm_projects : elles n'ont plus aucun
 * compte sur lequel se ranger, d'où cet encart séparé plutôt qu'une ligne du
 * tableau des comptes — qu'on lisait comme un compte de plus.
 *
 * ⚠️ Ce sont des montants HISTORIQUES, et le sous-titre doit continuer à le
 * dire : le cron ne mesure que les projets ENABLED non supprimés, donc
 * plus rien n'est mesuré après la disparition du projet. Présentés comme une
 * dépense courante, ils feraient chercher un dataset à couper qui n'est plus
 * suivi par personne.
 *
 * Liste rendue directement, sans dépliage : il y a quelques lignes, et un
 * accordéon fermé cachait la seule information de l'encart.
 */
export const OrphanUsageGroup: React.FC<{ range: PeriodRange }> = ({ range }) => {
	const { data, isLoading } = useGetList<OrphanUsageRow>('orphan-dw-usage', {
		pagination: { page: 1, perPage: ORPHANS_PER_PAGE },
		sort: { field: 'totalEur', order: 'DESC' },
		filter: { from: range.from, to: range.to },
	});

	const rows = data || [];
	if (isLoading || !rows.length) return null;

	return (
		<Card sx={{ mb: 3 }}>
			<CardContent sx={{ pb: 1 }}>
				<Typography variant="subtitle1" fontWeight={600}>
					Projets supprimés, compte inconnu ({rows.length})
				</Typography>
				<Typography variant="caption" color="text.secondary" display="block" sx={{ maxWidth: '60rem' }}>
					Consos mesurées dont le projet a disparu de la base : plus aucun billing account en
					face. Montants historiques — rien n'est mesuré après la suppression d'un projet.
					Total sur la plage :{' '}
					<Box component="span" sx={{ fontWeight: 700, fontSize: '0.9375rem', color: 'text.primary' }}>
						{formatEur(sum(rows, 'totalEur'))}
					</Box>{' '}
					dont {formatEur(sum(rows, 'storageEur'))} de storage.
				</Typography>
			</CardContent>
			<Box sx={{ overflowX: 'auto' }}>
				<Table size="small" sx={densitySx}>
					<TableHead>
						<TableRow>
							<TableCell>Folder ID</TableCell>
							<TableCell align="right">Storage</TableCell>
							<TableCell align="right">Conso</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((row) => (
							<OrphanRow key={row.id} row={row} />
						))}
					</TableBody>
				</Table>
			</Box>
		</Card>
	);
};
